import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  BeakerIcon,
  DocumentMagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import {
  getConflictStats,
  listSCGConflicts,
  getSCGConflict,
  startSCGConflictScan,
  getSCGConflictScanStatus,
  reviewSCGConflict,
  resolveSCGConflict,
  generateSCGConflictProbe,
} from '../services/api';
import SCGCitation from './SCGCitation';
import ViewInPdfButton from './ViewInPdfButton';
import EditSCGRuleModal from './EditSCGRuleModal';

// Map a rule object as returned by /api/v1/scg/conflicts/{id} to the shape
// EditSCGRuleModal expects (rule_id key + comma-joined arrays for keywords).
const toEditableRule = (raw) => {
  if (!raw) return null;
  return {
    rule_id: raw.rule_id || raw.id || '',
    classification_level: raw.classification_level || '',
    item: raw.item || '',
    description: raw.enhanced_description || raw.description || '',
    keywords: Array.isArray(raw.keywords) ? raw.keywords.join(', ') : (raw.keywords || ''),
    derivative_citation: raw.derivative_citation || '',
    compartment_caveats: Array.isArray(raw.compartment_caveats)
      ? raw.compartment_caveats.join(', ')
      : (raw.compartment_caveats || ''),
    dissemination_control: Array.isArray(raw.dissemination_control)
      ? raw.dissemination_control.join(', ')
      : (raw.dissemination_control || ''),
    reason: raw.reason || '',
    declassify_on: raw.declassify_on || '',
    remarks: raw.remarks || '',
    source: raw.source || raw.source_document?.filename || '',
  };
};

const SEVERITY_BADGE = {
  high: 'bg-red-100 text-red-800 border border-red-300',
  medium: 'bg-amber-100 text-amber-800 border border-amber-300',
  low: 'bg-gray-100 text-gray-700 border border-gray-300',
};

const VERDICT_BADGE = {
  real_conflict: 'bg-red-100 text-red-800 ring-1 ring-red-300',
  scope_difference: 'bg-blue-100 text-blue-800 ring-1 ring-blue-300',
  supersession: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300',
  false_positive: 'bg-gray-200 text-gray-600 ring-1 ring-gray-300',
  unjudged: 'bg-gray-200 text-gray-600 ring-1 ring-gray-300',
};

const truncateScg = (s, n = 36) => {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
};

// Pastel level-chip styles (NOT remapped by the dark-theme override layer, so
// they stay readable on the vault background — same palette as ManageSCG's
// rule badges).
const LEVEL_CHIP = {
  'TOP SECRET': 'bg-yellow-100 text-yellow-800 border border-yellow-300',
  TS: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
  SECRET: 'bg-red-100 text-red-800 border border-red-300',
  S: 'bg-red-100 text-red-800 border border-red-300',
  CONFIDENTIAL: 'bg-blue-100 text-blue-800 border border-blue-300',
  C: 'bg-blue-100 text-blue-800 border border-blue-300',
  UNCLASSIFIED: 'bg-green-100 text-green-800 border border-green-300',
  U: 'bg-green-100 text-green-800 border border-green-300',
};

const PrescribesPill = ({ prescribes }) => {
  if (!prescribes) return null;
  const lvl = (prescribes.level || '?').toUpperCase();
  const comp = (prescribes.compartments || []).join('/');
  const dis = (prescribes.dissem || []).join('/');
  const tail = [comp, dis].filter(Boolean).join('//');
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-xs">
      <span className={`px-1.5 py-0.5 rounded font-semibold ${LEVEL_CHIP[lvl] || 'bg-gray-100 text-gray-700 border border-gray-300'}`}>
        {lvl}
      </span>
      {tail && <span className="text-gray-600">{'//' + tail}</span>}
    </span>
  );
};

const ConflictDetail = ({ conflictId, onClose, onReviewed }) => {
  const queryClient = useQueryClient();
  const [editingRule, setEditingRule] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ['scg-conflict', conflictId],
    queryFn: () => getSCGConflict(conflictId),
    enabled: !!conflictId,
  });

  const reviewMutation = useMutation({
    mutationFn: (review) => reviewSCGConflict(conflictId, review),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scg-conflicts'] });
      queryClient.invalidateQueries({ queryKey: ['scg-conflict', conflictId] });
      queryClient.invalidateQueries({ queryKey: ['scg-conflict-stats'] });
      if (onReviewed) onReviewed();
    },
  });

  const probeMutation = useMutation({
    mutationFn: () => generateSCGConflictProbe(conflictId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scg-conflict', conflictId] });
      queryClient.invalidateQueries({ queryKey: ['scg-conflicts'] });
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (resolved) => resolveSCGConflict(conflictId, resolved),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scg-conflicts'] });
      queryClient.invalidateQueries({ queryKey: ['scg-conflict', conflictId] });
      queryClient.invalidateQueries({ queryKey: ['scg-conflict-stats'] });
    },
  });

  if (!conflictId) return null;

  return (
    <div className="fixed inset-0 z-40 bg-gray-900/70 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full flex items-start justify-center py-10 px-4">
        <div className="bg-white text-gray-900 rounded-xl shadow-2xl max-w-5xl w-full">
          <div className="flex items-center justify-between border-b border-gray-200 p-6 bg-white rounded-t-xl">
            <div className="flex items-center gap-3">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-500" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Cross-SCG Conflict</h2>
                <p className="text-xs text-gray-500 font-mono">{conflictId}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-600">
              <XCircleIcon className="w-7 h-7" />
            </button>
          </div>

          {isLoading || !data ? (
            <div className="p-12 text-center text-gray-600 bg-white">Loading…</div>
          ) : (
            <div className="p-6 space-y-6 bg-white text-gray-900">
              <div className="flex flex-wrap gap-2">
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium ${VERDICT_BADGE[data.verdict] || ''}`}>
                  {data.verdict.replace('_', ' ')}
                </span>
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium ${SEVERITY_BADGE[data.severity] || ''}`}>
                  severity: {data.severity}
                </span>
                {(data.structured_disagreement || []).map((axis) => (
                  <span key={axis} className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-purple-50 text-purple-700 ring-1 ring-purple-200">
                    Δ {axis}
                  </span>
                ))}
                {data.reviewed_by_admin && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300">
                    <CheckCircleIcon className="w-3.5 h-3.5" />
                    reviewed: {data.reviewed_by_admin}
                  </span>
                )}
                {data.resolved && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300">
                    <CheckCircleIcon className="w-3.5 h-3.5" />
                    completed{data.resolved_by ? ` by ${data.resolved_by}` : ''}
                  </span>
                )}
              </div>

              <div>
                <p className="text-xs uppercase tracking-wide text-gray-600 mb-1">Shared subject</p>
                <p className="text-base font-semibold text-gray-900">{data.subject || '—'}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Rule A */}
                <div className="border border-gray-300 rounded-lg p-4 bg-gray-100 text-gray-900">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs uppercase tracking-wide text-gray-700 font-semibold">Rule A</p>
                    <div className="flex items-center gap-2">
                      <ViewInPdfButton ruleId={data.rule_a} sourceInfo={data.rule_a_full} variant="icon" size="sm" />
                      <button
                        onClick={() => setEditingRule({ ...toEditableRule(data.rule_a_full), _orig_id: data.rule_a })}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-white border border-gray-300 text-gray-600 hover:bg-gray-50"
                      >
                        <PencilIcon className="w-3 h-3" /> Edit
                      </button>
                    </div>
                  </div>
                  <div className="mb-2"><SCGCitation ruleId={data.rule_a} compact={true} showClassificationBadge={false} /></div>
                  <p className="text-xs text-gray-600 mb-2 truncate" title={data.source_scg_a}>
                    📄 {truncateScg(data.source_scg_a, 60)}
                  </p>
                  <div className="my-2"><PrescribesPill prescribes={data.prescribes_a} /></div>
                  <p className="text-sm text-gray-900 mt-2">
                    <span className="font-medium">{data.rule_a_full?.item || '—'}</span>
                  </p>
                  {data.rule_a_full?.enhanced_description && (
                    <p className="text-xs text-gray-600 mt-1.5 line-clamp-4">{data.rule_a_full.enhanced_description}</p>
                  )}
                </div>

                {/* Rule B */}
                <div className="border border-gray-300 rounded-lg p-4 bg-gray-100 text-gray-900">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs uppercase tracking-wide text-gray-700 font-semibold">Rule B</p>
                    <div className="flex items-center gap-2">
                      <ViewInPdfButton ruleId={data.rule_b} sourceInfo={data.rule_b_full} variant="icon" size="sm" />
                      <button
                        onClick={() => setEditingRule({ ...toEditableRule(data.rule_b_full), _orig_id: data.rule_b })}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-white border border-gray-300 text-gray-600 hover:bg-gray-50"
                      >
                        <PencilIcon className="w-3 h-3" /> Edit
                      </button>
                    </div>
                  </div>
                  <div className="mb-2"><SCGCitation ruleId={data.rule_b} compact={true} showClassificationBadge={false} /></div>
                  <p className="text-xs text-gray-600 mb-2 truncate" title={data.source_scg_b}>
                    📄 {truncateScg(data.source_scg_b, 60)}
                  </p>
                  <div className="my-2"><PrescribesPill prescribes={data.prescribes_b} /></div>
                  <p className="text-sm text-gray-900 mt-2">
                    <span className="font-medium">{data.rule_b_full?.item || '—'}</span>
                  </p>
                  {data.rule_b_full?.enhanced_description && (
                    <p className="text-xs text-gray-600 mt-1.5 line-clamp-4">{data.rule_b_full.enhanced_description}</p>
                  )}
                </div>
              </div>

              {/* Rationale */}
              <div className="border border-amber-300 bg-amber-50 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wide text-amber-500 mb-1 font-semibold">LLM Adjudication</p>
                <p className="text-sm text-gray-900">{data.rationale || '—'}</p>
                {data.judge_model && (
                  <p className="text-xs text-amber-500 mt-2 font-mono">judge: {data.judge_model}</p>
                )}
              </div>

              {/* Probe doc */}
              <div className="border border-gray-300 rounded-lg p-4 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs uppercase tracking-wide text-gray-700 flex items-center gap-1 font-semibold">
                    <BeakerIcon className="w-4 h-4" /> Probe document (synthetic)
                  </p>
                  <button
                    onClick={() => probeMutation.mutate()}
                    disabled={probeMutation.isPending}
                    className="text-xs px-2.5 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-900 flex items-center gap-1 disabled:opacity-50"
                  >
                    <ArrowPathIcon className={`w-3.5 h-3.5 ${probeMutation.isPending ? 'animate-spin' : ''}`} />
                    {data.probe_document ? 'Regenerate' : 'Generate'}
                  </button>
                </div>
                {data.probe_document ? (
                  <p className="text-sm italic text-gray-900 leading-relaxed">{data.probe_document}</p>
                ) : (
                  <p className="text-sm text-gray-600">No probe document yet — click Generate to produce a synthetic example that would trigger both rules.</p>
                )}
              </div>

              {/* Review + lifecycle buttons */}
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-200">
                <p className="text-xs text-gray-700 mr-2 font-medium">Admin review:</p>
                {['real', 'scope_difference', 'false_positive'].map((r) => (
                  <button
                    key={r}
                    onClick={() => reviewMutation.mutate(r)}
                    disabled={reviewMutation.isPending}
                    className={`text-xs px-3 py-1.5 rounded font-medium transition disabled:opacity-50 ${
                      data.reviewed_by_admin === r
                        ? 'bg-emerald-600 text-white'
                        : 'bg-white border border-gray-400 text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    {r.replace('_', ' ')}
                  </button>
                ))}
                <div className="ml-auto">
                  {data.resolved ? (
                    <button
                      onClick={() => resolveMutation.mutate(false)}
                      disabled={resolveMutation.isPending}
                      className="text-xs px-3 py-1.5 rounded font-medium bg-white border border-gray-400 text-gray-900 hover:bg-gray-100 disabled:opacity-50"
                    >
                      Reopen
                    </button>
                  ) : (
                    <button
                      onClick={() => resolveMutation.mutate(true)}
                      disabled={resolveMutation.isPending}
                      className="text-xs px-3 py-1.5 rounded font-medium bg-emerald-600 hover:bg-emerald-500 text-white flex items-center gap-1 disabled:opacity-50"
                    >
                      <CheckCircleIcon className="w-4 h-4" />
                      Mark complete
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {editingRule && (
        <EditSCGRuleModal
          isOpen={!!editingRule}
          onClose={() => {
            setEditingRule(null);
            queryClient.invalidateQueries({ queryKey: ['scg-conflict', conflictId] });
            queryClient.invalidateQueries({ queryKey: ['scg-conflicts'] });
          }}
          rule={editingRule}
        />
      )}
    </div>
  );
};

const ScanRunner = ({ onScanComplete }) => {
  const queryClient = useQueryClient();
  const [jobId, setJobId] = useState(null);
  const [extractOnly, setExtractOnly] = useState(false);

  const { data: jobStatus } = useQuery({
    queryKey: ['scg-conflict-scan', jobId],
    queryFn: () => getSCGConflictScanStatus(jobId),
    enabled: !!jobId,
    refetchInterval: (q) => {
      const s = q.state.data?.state;
      return s === 'completed' || s === 'failed' ? false : 1500;
    },
  });

  const scanMutation = useMutation({
    mutationFn: () => startSCGConflictScan({ extract_only: extractOnly, generate_probes_top_n: 10 }),
    onSuccess: (data) => setJobId(data.job_id),
  });

  React.useEffect(() => {
    if (jobStatus?.state === 'completed') {
      queryClient.invalidateQueries({ queryKey: ['scg-conflicts'] });
      queryClient.invalidateQueries({ queryKey: ['scg-conflict-stats'] });
      if (onScanComplete) onScanComplete(jobStatus);
    }
  }, [jobStatus?.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const running = jobStatus?.state === 'running' || jobStatus?.state === 'queued';
  const pct = jobStatus?.progress_total
    ? Math.floor((100 * jobStatus.progress_done) / jobStatus.progress_total)
    : 0;

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white text-gray-900">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">Run conflict scan</p>
          <p className="text-xs text-gray-700">
            Scans every cross-SCG rule pair, detects prescription disagreements, and asks an LLM judge to classify them.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-gray-600 flex items-center gap-1">
            <input
              type="checkbox"
              checked={extractOnly}
              onChange={(e) => setExtractOnly(e.target.checked)}
              className="rounded"
            />
            extract schemas only
          </label>
          <button
            onClick={() => scanMutation.mutate()}
            disabled={running || scanMutation.isPending}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded disabled:opacity-50 flex items-center gap-2"
          >
            <DocumentMagnifyingGlassIcon className="w-4 h-4" />
            {running ? `Scanning… ${jobStatus?.stage || ''}` : 'Start Scan'}
          </button>
        </div>
      </div>
      {running && (
        <div className="mt-3">
          <div className="h-2 bg-gray-100 rounded overflow-hidden">
            <div className="h-full bg-indigo-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-1 text-xs text-gray-700">
            {jobStatus.stage}: {jobStatus.progress_done}/{jobStatus.progress_total} ({pct}%)
          </p>
        </div>
      )}
      {jobStatus?.state === 'completed' && (
        <div className="mt-3 text-xs text-emerald-900 bg-emerald-100 border border-emerald-300 rounded p-2">
          Scan complete — {JSON.stringify(jobStatus.summary)}
        </div>
      )}
      {jobStatus?.state === 'failed' && (
        <div className="mt-3 text-xs text-red-900 bg-red-100 border border-red-300 rounded p-2">
          Scan failed: {jobStatus.error}
        </div>
      )}
    </div>
  );
};

export default function SCGConflictsPanel() {
  const [verdictFilter, setVerdictFilter] = useState('real_conflict');
  const [severityFilter, setSeverityFilter] = useState('');
  const [reviewedFilter, setReviewedFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [openId, setOpenId] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const queryClient = useQueryClient();

  const rowResolveMutation = useMutation({
    mutationFn: ({ id, resolved }) => resolveSCGConflict(id, resolved),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scg-conflicts'] });
      queryClient.invalidateQueries({ queryKey: ['scg-conflict-stats'] });
    },
  });

  const { data: stats } = useQuery({
    queryKey: ['scg-conflict-stats'],
    queryFn: getConflictStats,
    refetchInterval: 30_000,
  });

  const { data: conflicts = [], isLoading } = useQuery({
    queryKey: ['scg-conflicts', verdictFilter, severityFilter, reviewedFilter, statusFilter],
    queryFn: () => listSCGConflicts({
      verdict: verdictFilter || undefined,
      severity: severityFilter || undefined,
      reviewed: reviewedFilter || undefined,
      status: statusFilter || undefined,
      limit: 200,
    }),
  });

  const toggleRow = (id) => setExpandedRows((s) => ({ ...s, [id]: !s[id] }));

  return (
    <div className="space-y-6 bg-white text-gray-900 p-6 rounded-lg border border-gray-200">
      {/* Stats header */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-xs uppercase tracking-wide text-gray-700 font-semibold">Total findings</p>
          <p className="text-2xl font-bold text-gray-900">{stats?.total ?? '—'}</p>
        </div>
        <div className="rounded-lg border border-red-300 bg-red-50 p-4">
          <p className="text-xs uppercase tracking-wide text-red-800 font-semibold">Real conflicts</p>
          <p className="text-2xl font-bold text-red-800">{stats?.by_verdict?.real_conflict ?? 0}</p>
        </div>
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
          <p className="text-xs uppercase tracking-wide text-amber-900 font-semibold">High severity</p>
          <p className="text-2xl font-bold text-amber-900">{stats?.by_severity?.high ?? 0}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-xs uppercase tracking-wide text-gray-700 font-semibold">Unreviewed</p>
          <p className="text-2xl font-bold text-gray-900">{stats?.real_conflicts_unreviewed ?? 0}</p>
        </div>
      </div>

      {/* SCG-pair breakdown */}
      {stats?.by_scg_pair && Object.keys(stats.by_scg_pair).length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 text-gray-900">
          <p className="text-xs uppercase tracking-wide text-gray-700 mb-2 font-semibold">Conflicts by SCG pair</p>
          <div className="space-y-1">
            {Object.entries(stats.by_scg_pair)
              .sort((a, b) => b[1] - a[1])
              .map(([pair, count]) => (
                <div key={pair} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 font-mono text-xs truncate" title={pair}>{pair}</span>
                  <span className="ml-3 px-2 py-0.5 rounded bg-gray-200 text-gray-900 text-xs font-medium">{count}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Scan runner */}
      <ScanRunner />

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center bg-white border border-gray-200 rounded-lg p-3">
        <select value={verdictFilter} onChange={(e) => setVerdictFilter(e.target.value)} className="text-sm border border-gray-300 rounded px-2 py-1 bg-white text-gray-900">
          <option value="">All verdicts</option>
          <option value="real_conflict">real_conflict</option>
          <option value="scope_difference">scope_difference</option>
          <option value="supersession">supersession</option>
          <option value="false_positive">false_positive</option>
          <option value="unjudged">unjudged</option>
        </select>
        <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} className="text-sm border border-gray-300 rounded px-2 py-1 bg-white text-gray-900">
          <option value="">All severities</option>
          <option value="high">high</option>
          <option value="medium">medium</option>
          <option value="low">low</option>
        </select>
        <select value={reviewedFilter} onChange={(e) => setReviewedFilter(e.target.value)} className="text-sm border border-gray-300 rounded px-2 py-1 bg-white text-gray-900">
          <option value="">Any review state</option>
          <option value="unreviewed">Unreviewed only</option>
          <option value="reviewed">Reviewed only</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-sm border border-gray-300 rounded px-2 py-1 bg-white text-gray-900">
          <option value="open">Open</option>
          <option value="resolved">Completed</option>
          <option value="">All</option>
        </select>
        <span className="ml-auto text-xs text-gray-700">{conflicts.length} shown</span>
      </div>

      {/* Conflict list */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-700 bg-white">Loading…</div>
        ) : conflicts.length === 0 ? (
          <div className="p-8 text-center text-gray-700 bg-white">
            No conflicts match the current filters. {stats?.total === 0 && 'Click "Start Scan" above to run the detector.'}
          </div>
        ) : (
          <table className="w-full text-sm bg-white text-gray-900">
            <thead className="bg-gray-100 border-b border-gray-200">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-700">
                <th className="p-3 w-8"></th>
                <th className="p-3">Subject</th>
                <th className="p-3">Rule A → B</th>
                <th className="p-3">Δ</th>
                <th className="p-3">Sev</th>
                <th className="p-3">Verdict</th>
                <th className="p-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {conflicts.map((c) => {
                const open = !!expandedRows[c.id];
                return (
                  <React.Fragment key={c.id}>
                    <tr className="border-b border-gray-100 hover:bg-gray-50 bg-white">
                      <td className="p-3">
                        <button onClick={() => toggleRow(c.id)} className="text-gray-500 hover:text-gray-900">
                          {open ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="p-3 font-medium text-gray-900">{c.subject}</td>
                      <td className="p-3 text-xs">
                        <div className="flex items-center gap-1 flex-wrap">
                          <SCGCitation ruleId={c.rule_a} compact={true} showClassificationBadge={false} />
                          <ViewInPdfButton ruleId={c.rule_a} variant="icon" size="sm" />
                          <span className="text-gray-500">↔</span>
                          <SCGCitation ruleId={c.rule_b} compact={true} showClassificationBadge={false} />
                          <ViewInPdfButton ruleId={c.rule_b} variant="icon" size="sm" />
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {(c.structured_disagreement || []).map((a) => (
                            <span key={a} className="px-1.5 py-0.5 text-[10px] rounded bg-purple-50 text-purple-800 ring-1 ring-purple-300">{a}</span>
                          ))}
                        </div>
                      </td>
                      <td className="p-3">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${SEVERITY_BADGE[c.severity] || ''}`}>
                          {c.severity}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${VERDICT_BADGE[c.verdict] || ''}`}>
                            {c.verdict.replace('_', ' ')}
                          </span>
                          {c.resolved && (
                            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300">
                              <CheckCircleIcon className="w-3 h-3" /> done
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            onClick={() => setOpenId(c.id)}
                            className="text-xs px-2.5 py-1 rounded bg-indigo-600 hover:bg-indigo-500 text-white font-medium"
                          >
                            Details
                          </button>
                          {c.resolved ? (
                            <button
                              onClick={() => rowResolveMutation.mutate({ id: c.id, resolved: false })}
                              disabled={rowResolveMutation.isPending}
                              title="Reopen this conflict"
                              className="text-xs px-2.5 py-1 rounded border border-gray-400 text-gray-900 bg-white hover:bg-gray-100 font-medium disabled:opacity-50"
                            >
                              Reopen
                            </button>
                          ) : (
                            <button
                              onClick={() => rowResolveMutation.mutate({ id: c.id, resolved: true })}
                              disabled={rowResolveMutation.isPending}
                              title="Mark complete — removes it from the open list"
                              className="text-xs px-2.5 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white font-medium inline-flex items-center gap-1 disabled:opacity-50"
                            >
                              <CheckCircleIcon className="w-3.5 h-3.5" /> Complete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {open && (
                      <tr className="border-b border-gray-100 bg-gray-100">
                        <td className="bg-gray-100"></td>
                        <td colSpan={6} className="p-3 text-xs text-gray-900 bg-gray-100">
                          <p className="mb-1"><span className="font-semibold">Rationale:</span> {c.rationale}</p>
                          <p className="mb-1"><span className="font-semibold">SCG A:</span> {truncateScg(c.source_scg_a, 80)}</p>
                          <p><span className="font-semibold">SCG B:</span> {truncateScg(c.source_scg_b, 80)}</p>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {openId && (
        <ConflictDetail
          conflictId={openId}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}
